/* The product-page dispatch countdown.

   This is the reference implementation from section 4 of the spec, with the
   configuration lifted out of the code and into data attributes so the cut-off,
   the holiday list and all four lines of copy live in one place -
   snippets/dico-dispatch-countdown.liquid - rather than being duplicated here
   where they would drift.

   THE CUT-OFF IN THE VISITOR'S OWN CLOCK, BUT ONLY WHEN IT DIFFERS
   "Dispatched before 14:00" is a Copenhagen fact stated as though it were
   everyone's. To a customer in New York it is not their two o'clock and was
   not labelled as anyone else's, so the deadline is converted back out of the
   instant into the browser's timezone: 8:00 AM there, the same moment.

   It is only rewritten when the browser's offset actually differs from
   Copenhagen's at that instant. The first version rewrote it always, and a
   visitor in Berlin - the same clock as Copenhagen - watched "14:00" turn into
   "02:00 PM" on load, because toLocaleTimeString follows the browser's LOCALE
   as well as its zone, and plenty of Danish and German browsers are set to
   English. Same moment, same clock, pointless flicker, worse formatting. So
   when the offsets match, the server's own rendering stands.

   The day word moves the same way, from the deadline's date in the visitor's
   calendar rather than Copenhagen's, so "we ship it today" is never said to
   someone for whom it is already tomorrow.

   WHY THE STATE IS WORKED OUT HERE AND NOT JUST ON THE SERVER
   Liquid renders the first paint, so the page is already correct before this
   file runs. But the countdown has to keep running and roll over on its own: at
   14:00 the same-day promise becomes the next working day, and at midnight the
   day word moves on. Recomputing the whole state each tick means those
   transitions need no special case - the next tick simply returns a different
   answer.

   It also fixes the clock change properly. Denmark drops to CET on 25 Oct 2026,
   and the countdown to the next working day's cut-off spans that change twice a
   year. Deriving Copenhagen's offset from the IANA database at the relevant
   instant - which is what tzOffset does - is right through both changes;
   arithmetic on wall-clock seconds is an hour out on those two days.

   THE ONE THING THE SERVER IS STILL TRUSTED FOR
   A visitor whose clock is badly wrong would be shown a confidently wrong ship
   day. So the server stamps the instant it rendered, and if the browser's clock
   disagrees by more than six hours this file does nothing at all and leaves the
   server's sentence on screen. Being frozen is better than being wrong.

   AFTER THE CUT-OFF IT KEEPS COUNTING
   "Order now, ships tomorrow - next dispatch in 16h 04m" rather than a dead
   line. The urgency never fully drops, which is the point of having it.

   An earlier version stopped at the cut-off, because it had the opposite bug:
   it counted down to tomorrow's 14:00 while still showing the same-day wording,
   so at six in the evening the page read "Order within 19h 47m and we ship it
   today". The lesson is that the deadline and the wording have to come from the
   same decision, which they now do - both fall out of dispatchState().

   TICK RATE
   Every second while seconds are on screen, every minute when they are not.
   A one-second interval on a sixteen-hour countdown is pure battery. */
(function () {
    'use strict';

    var SELECTOR = '[data-dico-dc]';
    var MAX_SKEW = 6 * 60 * 60 * 1000;

    /* Offset of a timezone at a given instant, in ms. */
    function tzOffset(date, tz) {
        var p = new Intl.DateTimeFormat('en-US', {
            timeZone: tz, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        }).formatToParts(date).reduce(function (a, x) { a[x.type] = x.value; return a; }, {});

        var asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
        return asUTC - date.getTime();
    }

    /* A wall-clock time in tz -> the real UTC instant. Two passes settles the
       DST edges, where the first guess can land on the wrong side of a jump. */
    function wallToInstant(y, m, d, h, min, tz) {
        var t = Date.UTC(y, m - 1, d, h, min);
        for (var i = 0; i < 2; i++) {
            t = Date.UTC(y, m - 1, d, h, min) - tzOffset(new Date(t), tz);
        }
        return new Date(t);
    }

    /* Copenhagen's calendar fields for an instant. */
    function wallParts(date, tz) {
        var p = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        }).formatToParts(date).reduce(function (a, x) { a[x.type] = x.value; return a; }, {});

        return {
            y: +p.year, m: +p.month, d: +p.day,
            h: +p.hour % 24, min: +p.minute,
            iso: p.year + '-' + p.month + '-' + p.day
        };
    }

    function isWorkingDay(iso, holidays) {
        var wd = new Date(iso + 'T12:00:00Z').getUTCDay();
        return wd !== 0 && wd !== 6 && holidays.indexOf(iso) === -1;
    }

    /* The whole rule, in Copenhagen - because the warehouse is in Copenhagen. */
    function dispatchState(now, cfg) {
        var p = wallParts(now, cfg.tz);

        if (isWorkingDay(p.iso, cfg.holidays) && p.h < cfg.cutoff) {
            return {
                sameDay: true,
                deadline: wallToInstant(p.y, p.m, p.d, cfg.cutoff, 0, cfg.tz)
            };
        }

        var c = new Date(Date.UTC(p.y, p.m - 1, p.d));
        do {
            c.setUTCDate(c.getUTCDate() + 1);
        } while (!isWorkingDay(c.toISOString().slice(0, 10), cfg.holidays));

        return {
            sameDay: false,
            deadline: wallToInstant(c.getUTCFullYear(), c.getUTCMonth() + 1, c.getUTCDate(), cfg.cutoff, 0, cfg.tz)
        };
    }

    /* Minutes and seconds under the hour reads as more urgent than "0h 43m". */
    function formatLeft(ms) {
        var t = Math.max(0, Math.floor(ms / 1000));
        var h = Math.floor(t / 3600);
        var m = Math.floor((t % 3600) / 60);
        var s = t % 60;

        if (h > 0) { return h + 'h ' + pad(m) + 'm'; }
        if (m > 0) { return m + ' min ' + pad(s) + 's'; }
        return s + 's';
    }

    function pad(n) {
        return n < 10 ? '0' + n : String(n);
    }

    /* Same clock as the warehouse: leave the server's wording alone. Different
       clock: show the same instant where the visitor lives. */
    function localTime(deadline, cfg) {
        var here = -deadline.getTimezoneOffset() * 60000;
        if (here === tzOffset(deadline, cfg.tz)) { return cfg.cutoffLocal; }

        return deadline.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }

    /* Which day the dispatch lands on in the visitor's calendar, not ours. */
    function localDayLabel(deadline, now) {
        if (deadline.toDateString() === now.toDateString()) { return 'today'; }

        var t = new Date(now.getTime());
        t.setDate(t.getDate() + 1);
        if (deadline.toDateString() === t.toDateString()) { return 'tomorrow'; }

        return deadline.toLocaleDateString([], { weekday: 'long' });
    }

    function config(el) {
        return {
            tz: el.getAttribute('data-tz') || 'Europe/Copenhagen',
            cutoff: parseInt(el.getAttribute('data-cutoff'), 10) || 14,
            cutoffLocal: el.getAttribute('data-cutoff-local') || '',
            holidays: (el.getAttribute('data-holidays') || '').split(',').filter(Boolean),
            sameMain: el.getAttribute('data-same-main') || '',
            sameSub: el.getAttribute('data-same-sub') || '',
            afterMain: el.getAttribute('data-after-main') || '',
            afterSub: el.getAttribute('data-after-sub') || ''
        };
    }

    function fill(text, time, day, local) {
        return text
            .replace('[time]', time)
            .replace('[day]', day)
            .replace('[localtime]', local);
    }

    function render(el, cfg) {
        var now = new Date();
        var st = dispatchState(now, cfg);
        var left = st.deadline - now;

        var time = formatLeft(left);
        var day = localDayLabel(st.deadline, now);
        var local = localTime(st.deadline, cfg);

        var main = st.sameDay ? cfg.sameMain : cfg.afterMain;
        var sub = st.sameDay ? cfg.sameSub : cfg.afterSub;

        var html = fill(main, '<b>' + time + '</b>', '<b>' + day + '</b>', local) +
            '<span class="sub">' + fill(sub, time, day, local) + '</span>';

        if (el.innerHTML !== html) { el.innerHTML = html; }
        el.classList.toggle('is-urgent', st.sameDay && left < 3600000);

        return left;
    }

    function start(el) {
        if (el.dicoRunning) { return; }
        el.dicoRunning = true;

        /* A clock this far out would produce a confident, wrong ship day. */
        var serverNow = parseInt(el.getAttribute('data-server-now'), 10);
        if (serverNow && Math.abs(Date.now() - serverNow * 1000) > MAX_SKEW) { return; }

        var cfg = config(el);

        (function tick() {
            var left = render(el, cfg);
            /* Seconds on screen, seconds on the clock. */
            window.setTimeout(tick, left < 3600000 ? 1000 : 60000);
        }());
    }

    function scan() {
        var nodes = document.querySelectorAll(SELECTOR);
        for (var i = 0; i < nodes.length; i++) { start(nodes[i]); }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scan);
    } else {
        scan();
    }

    /* The theme re-renders the product form when a size is picked, and the
       theme editor re-renders whole sections; either can replace this element
       with a fresh one that has never been started. */
    document.addEventListener('shopify:section:load', scan);

    if (window.MutationObserver) {
        var watched = document.getElementById('mainContent');
        if (watched) {
            var queued = null;
            new MutationObserver(function () {
                if (queued) { window.clearTimeout(queued); }
                queued = window.setTimeout(function () { queued = null; scan(); }, 120);
            }).observe(watched, { childList: true, subtree: true });
        }
    }
}());